/**
 * Create or edit a back office user. Left column: accesses, identity, role,
 * language, MTM settings; right column: assigned stores (hidden for the
 * "workshop" role, which is filtered by workshops instead).
 */
import { useState, type SubmitEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { ROLES, STORAGE_KEYS, type Platform } from '@/shared/constants';
import type { Store } from '@/shared/config/app-config';
import { ApiError } from '@/shared/api/client';
import { local } from '@/shared/lib/storage';
import { getUserProfile } from '@/features/auth/session';
import { workshopsQueryOptions } from '@/features/stores/api';
import { AssignedStoresField } from '@/features/info/AssignedStoresField';
import { SelectField } from '@/shared/ui/SelectField';
import { Dialog } from '@/shared/ui/Dialog';
import { Select } from '@/shared/ui/Select';
import { optionsOf } from '@/shared/ui/select-options';
import { createUser, updateUser, type UserRecord } from './api';
import {
  emptyUserForm,
  formToUser,
  MEASUREMENTS_OPTIONS,
  roleOptions,
  userToForm,
  validateUserForm,
  type UserFormErrors,
  type UserFormValues,
} from './user-form';
import { kebabCase } from '@/shared/lib/kebab-case';

export interface UserDialogProps {
  item: UserRecord | null;
  platform: Platform;
  stores: readonly Store[];
  /** Languages of the application config (`config.languages`). */
  languages: readonly string[];
  readOnly?: boolean;
  onClose: (result: 'Confirm' | 'Cancel') => void;
}

/** The API answers a plain-text reason on failure (e.g. "User already exists"). */
const apiErrorText = (error: unknown): string | null =>
  error instanceof ApiError && typeof error.body === 'string' && error.body
    ? error.body
    : null;

const dialogTitle = (readOnly: boolean, creating: boolean): string => {
  if (readOnly) return 'User info';
  return creating ? 'User creation' : 'User edit';
};

export function UserDialog({
  item,
  platform,
  stores,
  languages,
  readOnly = false,
  onClose,
}: Readonly<UserDialogProps>) {
  const creating = !item;
  const currentUser = getUserProfile();
  const [values, setValues] = useState<UserFormValues>(() =>
    item ? userToForm(item, platform) : emptyUserForm(platform),
  );
  const [assignedStores, setAssignedStores] = useState<string[]>(
    () => item?.assignedStores ?? [],
  );
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<UserFormErrors>({});
  // Red border once a field has been edited and is invalid.
  const [dirty, setDirty] = useState<Set<keyof UserFormValues>>(
    () => new Set(),
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const workshops = useQuery(workshopsQueryOptions());
  const sortedWorkshops = [...(workshops.data ?? [])].sort((a, b) =>
    (a.name ?? '').localeCompare(b.name ?? ''),
  );
  const sortedLanguages = [...languages].sort((a, b) => a.localeCompare(b));

  const set = <K extends keyof UserFormValues>(
    key: K,
    value: UserFormValues[K],
  ) => {
    setValues((prev) => {
      const next = { ...prev, [key]: value };
      // Remove role if it is no longer valid for the current dmsAccess setting.
      if (
        next.dmsAccess &&
        !roleOptions(currentUser?.role, true).some(
          (role) => role.value === next.role,
        )
      ) {
        next.role = '';
      }
      return next;
    });
    setDirty((prev) => new Set(prev).add(key));
  };
  const liveErrors = validateUserForm(values);
  const invalidClass = (key: keyof UserFormErrors) =>
    dirty.has(key) && liveErrors[key] ? 'invalid' : '';
  const fieldError = (key: keyof UserFormErrors) => submitted && errors[key];

  const save = useMutation({
    mutationFn: async () => {
      const data = formToUser(values, assignedStores);
      if (item) {
        // The stored profile is refreshed before the request, when editing oneself.
        if (currentUser && item.id === currentUser.id) {
          local.setJson(STORAGE_KEYS.USER, { ...currentUser, ...values });
        }
        await updateUser({ ...data, id: item.id });
      } else {
        await createUser(data);
      }
    },
    onSuccess: () => onClose('Confirm'),
    onError: (error) =>
      setErrorMessage(
        apiErrorText(error) ??
          (creating ? 'Failed to create user' : 'Failed to update user'),
      ),
  });

  const onSubmit = (event: SubmitEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setErrorMessage(null);
    if (!values.abcdiorAccess && !values.dmsAccess) {
      setErrorMessage('At least one platform access is required');
      return;
    }
    const nextErrors = validateUserForm(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      setErrorMessage('Please fix the errors');
      return;
    }
    save.mutate();
  };

  const title = dialogTitle(readOnly, creating);
  const roles = roleOptions(currentUser?.role, values.dmsAccess);
  const isWorkshop = values.role === ROLES.WORKSHOP;
  // DHL cannot update admins.
  const saveForbidden =
    item?.role === ROLES.ADMIN && currentUser?.role === ROLES.DHL;

  const checkbox = (
    id: 'abcdiorAccess' | 'dmsAccess' | 'disabled' | 'villaDior',
    domId: string,
    label: string,
  ) => (
    <div className="styled-input-square styled-input-single">
      <input
        type="checkbox"
        id={domId}
        name={id}
        data-testid={`user-form-${kebabCase(id)}`}
        checked={values[id]}
        onChange={(event) => set(id, event.target.checked)}
      />
      <label htmlFor={domId}>{label}</label>
    </div>
  );

  const select = (
    key: 'role' | 'language' | 'measurements',
    label: string,
    placeholder: string,
    options: readonly { value: string; label: string }[],
    error?: keyof UserFormErrors,
  ) => (
    <SelectField
      id={`user-${key}`}
      name={key}
      label={label}
      placeholder={placeholder}
      options={options}
      value={values[key]}
      onChange={(value) => set(key, value)}
      fieldClassName={error ? invalidClass(error) : ''}
      error={error ? (errors[error] ?? '') : undefined}
      showError={!!error && !!fieldError(error)}
      testId={`user-form-${key}`}
    />
  );

  return (
    <Dialog
      title={title}
      onClose={() => onClose('Cancel')}
      width="800px"
      testId="user-dialog"
    >
      <form className="form popin-form" onSubmit={onSubmit} noValidate>
        <fieldset disabled={readOnly} className="m-0 min-w-0 border-0 p-0">
          <div className="bs-row">
            <div className="col-span-5">
              <div className="bs-row mb-1">
                <div className="col-span-4">
                  {checkbox('abcdiorAccess', 'abcdiorAccess', 'ABCDior')}
                </div>
                <div className="col-span-4">
                  {checkbox('dmsAccess', 'dmsAccess', 'MTM')}
                </div>
              </div>
              <div className="mb-2">
                <label className="field-label" htmlFor="user-username">
                  User Name
                </label>
                <input
                  id="user-username"
                  data-testid="user-form-username"
                  className={`field ${invalidClass('username')}`}
                  name="username"
                  placeholder="User Name"
                  value={values.username}
                  onChange={(event) => set('username', event.target.value)}
                />
                <small
                  hidden={!fieldError('username')}
                  data-testid="user-form-username-error"
                >
                  {errors.username}
                </small>
              </div>
              <div className="mb-2">
                <label className="field-label" htmlFor="user-email">
                  Email
                </label>
                <input
                  id="user-email"
                  data-testid="user-form-email"
                  className={`field ${invalidClass('email')}`}
                  name="email"
                  placeholder="Email"
                  value={values.email}
                  onChange={(event) => set('email', event.target.value)}
                />
                <small
                  hidden={!fieldError('email')}
                  data-testid="user-form-email-error"
                >
                  {errors.email}
                </small>
              </div>
              {select('role', 'Role', '--Select role--', roles, 'role')}
              {select(
                'language',
                'Default language',
                '--Select language--',
                optionsOf(sortedLanguages),
                'language',
              )}
              {values.dmsAccess &&
                select(
                  'measurements',
                  'Measurements',
                  '--Select measurements--',
                  MEASUREMENTS_OPTIONS,
                )}
              {isWorkshop && (
                <div className="mb-2">
                  <label className="field-label" htmlFor="user-workshopIds">
                    Filter workshops
                  </label>
                  <div className="field field-selector">
                    {workshops.isSuccess ? (
                      <Select
                        multiple
                        id="user-workshopIds"
                        name="workshopIds"
                        placeholder="--Select workshops--"
                        options={sortedWorkshops.map((workshop) => ({
                          value: workshop.id,
                          label: workshop.name ?? '',
                        }))}
                        value={values.workshopIds}
                        onChange={(ids) => set('workshopIds', ids)}
                        testId="user-form-workshops"
                      />
                    ) : (
                      <FontAwesomeIcon icon={faCircleNotch} spin />
                    )}
                  </div>
                </div>
              )}
              <div className="bs-row mb-2 mt-4">
                <div className="col-span-6">
                  {checkbox('disabled', 'is-disabled', 'Disable This User')}
                </div>
                {values.dmsAccess && (
                  <div className="col-span-6">
                    {checkbox('villaDior', 'villaDior', 'Villa Dior')}
                  </div>
                )}
              </div>
              {errorMessage && (
                <div
                  className="alert mb-0"
                  role="alert"
                  data-testid="user-form-error"
                >
                  <span className="error-msg">{errorMessage}</span>
                </div>
              )}
              {!readOnly && (
                <div className="mt-6 flex justify-center">
                  <button
                    className="button button-form"
                    type="submit"
                    disabled={save.isPending || saveForbidden}
                    data-testid="user-form-save-button"
                  >
                    Save
                  </button>
                </div>
              )}
            </div>
            <div className="col-span-7">
              <AssignedStoresField
                stores={stores}
                selectedIds={assignedStores}
                onChange={setAssignedStores}
                readOnly={readOnly}
                hideTree={isWorkshop}
                testId="user-form-assigned-stores"
              />
            </div>
          </div>
        </fieldset>
      </form>
    </Dialog>
  );
}
